import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'
import loadable from '@loadable/component'

import { getPlain } from '../../../../../store/utils'
import { useTranslate } from '../../../../../utils/translate'
import DetailedProductView from './DetailedProductView'
import SimpleProductView from './SimpleProductView'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './ProductSettings'))

const productViews = {
  simple: SimpleProductView,
  detailed: DetailedProductView,
}

export default function ProductPlugin({
  config,
  data,
  editorMode,
  editorView,
  onCancel,
  onDataChange,
  onEdit,
  onSave,
}: Readonly<WorkspacePluginProps>): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null)
  const [error] = useState(false)
  const [isActive, setIsActive] = useState(false)

  const product = useSelector<State, Frontend.Product | undefined>((state) =>
    getPlain(state.getIn(['productData', 'productPlugin', data.get('productId')])),
  )

  const t = useTranslate('interface', 'components.productPluginComponent')

  const pluginActiveClasses = cc(['dali-plugin-productshowcase', { 'dali-grid-element-highlighted': isActive }])

  if (error || (!editorView && !product)) {
    return null
  }

  const ProductView = productViews[data.get('layout')]

  return (
    <div className={pluginActiveClasses} ref={ref}>
      <SettingsLayer
        open={editorMode === 'edit'}
        virtualRef={ref}
        side="top"
        align="center"
        onEscapeKeyDown={(event) => (event.target as HTMLElement).tagName !== 'INPUT' && onCancel()}
        className="dali-settingslayer-productshowcase"
        onActiveStateChange={setIsActive}
      >
        {({ renderLayout }) => <SettingsForm {...{ data, config, onDataChange, onCancel, onSave, renderLayout, t }} />}
      </SettingsLayer>
      {product && <ProductView product={product} />}
      {!product && (
        <div className="dali-grid-element-placeholder">
          <span className="dali-grid-element-placeholder-icon dali-plugin-productshowcase-placeholder-icon" />
          <button className="dali-grid-element-placeholder-button" type="button" onClick={() => onEdit()}>
            {t('selectProductButton.label')}
          </button>
        </div>
      )}
    </div>
  )
}
